import React, { Component } from 'react'
import InvoiceProductForm from './InvoiceProductForm'
import { InvoiceService } from '../../../services'
import { startFetch, endFetch } from '../../../actions/generalActions'
import { connect } from 'react-redux'
import ToastService from '../shared/Toast/ToastService'
import { prefix } from '../../../i18n'

const tInvoice = prefix('invoice')

// react-select compares options by `getOptionValue`, so a selection has to be
// the option instance from the fetched list to render as selected. These
// resolve a known value against that list, falling back to the original value
// when the list doesn't contain it (a category/product no longer available).
function findByName(options, name) {
  if (!name) return null
  return options.find(function(o) { return o.name === name }) || null
}

function findByIdOrName(options, value) {
  if (!value) return null
  return options.find(function(o) {
    return o.id === value.id
      || (value.name && o.name === value.name)
      || (value.external_identifier && o.external_identifier === value.external_identifier)
  }) || value
}

class InvoiceProductFormContainer extends Component {
  state = {
    brands: [],
    groups: [],
    categories: [],
    products: [],
    saleTypes: [],
    selectedBrand: null,
    selectedGroup: null,
    selectedCategory: null,
    selectedProduct: null,
    selectedSaleType: null,
  }

  clientId() {
    return this.props.client && this.props.client.slug
  }

  // The modal's "Tipo de venda" list is NOT the header's sale types from
  // /invoices/get_sale_types — those are a different entity whose ids are not
  // product category ids. It is the set of root client product categories,
  // which is what DefaultTemplate feeds into this dropdown (see its
  // UserContainer.getClientProducts → saveBrands → mapStateToProps saleTypes).
  // Marca is then the first level beneath the chosen root.
  componentDidMount() {
    const { initialValues } = this.props
    const clientId = this.clientId()

    this.props.startFetch()
    InvoiceService.getClientCategories({ clientId: clientId, parentId: null })
      .then(function(response) {
        const saleTypes = response.data.client_categories || []
        this.setState({ saleTypes: saleTypes })
        this.props.endFetch()
        if (initialValues) {
          this.prefillFromInitialValues(initialValues, saleTypes)
        }
      }.bind(this))
      .catch(function() {
        this.props.endFetch()
        ToastService.error('Erro ao carregar as opções de produto')
      }.bind(this))
  }

  // Edit mode: re-hydrate the cascade (Marca/Grupo/Categoria/Produto option
  // lists) for a product that was added earlier, without resetting the already
  // known selections (unlike the user-driven handleXChange handlers below).
  //
  // A locally accumulated product carries the full option objects, but a
  // persisted invoice_detail only carries the category ancestry as names, so
  // each level is resolved by name against the list fetched for its parent —
  // which means the levels must be fetched in sequence rather than in parallel.
  // The hierarchy is non-strict: resolution stops at whichever level the
  // detail actually reaches, and Produto is fetched against that leaf.
  prefillFromInitialValues = (initialValues, saleTypes) => {
    const clientId = this.clientId()
    const selectedSaleType = initialValues.selectedSaleType
      ? findByIdOrName(saleTypes, initialValues.selectedSaleType)
      : findByName(saleTypes, initialValues.saleTypeName)

    this.setState({ selectedSaleType: selectedSaleType })
    if (!selectedSaleType) return

    InvoiceService.getClientCategories({ clientId: clientId, parentId: selectedSaleType.id })
      .then(function(response) {
        const brands = response.data.client_categories || []
        const selectedBrand = initialValues.selectedBrand
          ? findByIdOrName(brands, initialValues.selectedBrand)
          : findByName(brands, initialValues.brandName)
        this.setState({ brands: brands, selectedBrand: selectedBrand })
        if (!selectedBrand) return null
        return InvoiceService.getClientCategories({ clientId: clientId, parentId: selectedBrand.id })
          .then(function(groupsResponse) {
            const groups = groupsResponse.data.client_categories || []
            const selectedGroup = initialValues.selectedGroup
              ? findByIdOrName(groups, initialValues.selectedGroup)
              : findByName(groups, initialValues.groupName)
            this.setState({ groups: groups, selectedGroup: selectedGroup })
            if (!selectedGroup) return null
            return InvoiceService.getClientCategories({ clientId: clientId, parentId: selectedGroup.id })
              .then(function(categoriesResponse) {
                const categories = categoriesResponse.data.client_categories || []
                const selectedCategory = initialValues.selectedCategory
                  ? findByIdOrName(categories, initialValues.selectedCategory)
                  : findByName(categories, initialValues.categoryName)
                this.setState({ categories: categories, selectedCategory: selectedCategory })
                return selectedCategory
              }.bind(this))
          }.bind(this))
      }.bind(this))
      .then(function() {
        const { selectedBrand, selectedGroup, selectedCategory } = this.state
        const leaf = selectedCategory || selectedGroup || selectedBrand || selectedSaleType
        return InvoiceService.getClientProducts({ clientId: clientId, parentId: leaf.id })
          .then(function(response) {
            const products = response.data.client_products || []
            this.setState({
              products: products,
              selectedProduct: findByIdOrName(products, initialValues.selectedProduct),
            })
          }.bind(this))
      }.bind(this))
      .catch(function() { ToastService.error('Erro ao carregar as opções de produto') })
  }

  // Sale type selected → fetch the Marca list beneath it (the sale type is the
  // root of the client category tree, so Marca is its first level), plus the
  // products attached directly to it. Resets everything downstream.
  handleSaleTypeChange = (saleType) => {
    this.setState({
      selectedSaleType: saleType,
      selectedBrand: null,
      selectedGroup: null,
      selectedCategory: null,
      selectedProduct: null,
      brands: [],
      groups: [],
      categories: [],
      products: [],
    })
    if (!saleType) return
    const clientId = this.clientId()
    Promise.all([
      InvoiceService.getClientCategories({ clientId: clientId, parentId: saleType.id }),
      InvoiceService.getClientProducts({ clientId: clientId, parentId: saleType.id }),
    ])
      .then(function(results) {
        this.setState({
          brands: results[0].data.client_categories || [],
          products: results[1].data.client_products || [],
        })
      }.bind(this))
      .catch(function() { ToastService.error('Erro ao carregar as marcas e produtos') })
  }

  // Brand selected → fetch its Groups AND its Products in parallel (hierarchy
  // is non-strict: some Brands have no Groups/Categories beneath them, so
  // Products are always fetched against the deepest selection made so far).
  // Resets Group/Category/Product selections.
  handleBrandChange = (brand) => {
    this.setState({
      selectedBrand: brand,
      selectedGroup: null,
      selectedCategory: null,
      selectedProduct: null,
      groups: [],
      categories: [],
      products: [],
    })
    if (!brand) return
    const clientId = this.clientId()
    Promise.all([
      InvoiceService.getClientCategories({ clientId: clientId, parentId: brand.id }),
      InvoiceService.getClientProducts({ clientId: clientId, parentId: brand.id }),
    ])
      .then(function(results) {
        this.setState({
          groups: results[0].data.client_categories || [],
          products: results[1].data.client_products || [],
        })
      }.bind(this))
      .catch(function() { ToastService.error('Erro ao carregar os grupos e produtos') })
  }

  // Group selected → fetch its Categories AND its Products in parallel.
  // Resets Category/Product selections.
  handleGroupChange = (group) => {
    this.setState({
      selectedGroup: group,
      selectedCategory: null,
      selectedProduct: null,
      categories: [],
      products: [],
    })
    if (!group) return
    const clientId = this.clientId()
    Promise.all([
      InvoiceService.getClientCategories({ clientId: clientId, parentId: group.id }),
      InvoiceService.getClientProducts({ clientId: clientId, parentId: group.id }),
    ])
      .then(function(results) {
        this.setState({
          categories: results[0].data.client_categories || [],
          products: results[1].data.client_products || [],
        })
      }.bind(this))
      .catch(function() { ToastService.error('Erro ao carregar as categorias e produtos') })
  }

  // Category selected → fetch its Products, resetting Product
  handleCategoryChange = (category) => {
    this.setState({ selectedCategory: category, selectedProduct: null, products: [] })
    if (!category) return
    const clientId = this.clientId()
    InvoiceService.getClientProducts({ clientId: clientId, parentId: category.id })
      .then(function(response) {
        this.setState({ products: response.data.client_products || [] })
      }.bind(this))
      .catch(function() { ToastService.error('Erro ao carregar os produtos') })
  }

  handleProductChange = (product) => {
    this.setState({ selectedProduct: product })
  }

  handleSubmit = (e) => {
    e.preventDefault()
    const { selectedBrand, selectedGroup, selectedCategory, selectedProduct, selectedSaleType } = this.state
    const quantity = e.target.quantity ? e.target.quantity.value : 1
    const unitPrice = e.target.unit_price ? e.target.unit_price.value : ''

    // Marca is not required: the hierarchy is non-strict, so a product can be
    // attached directly to the sale type with no Marca beneath it.
    if (!selectedSaleType) { ToastService.error(tInvoice('select-sale-type')); return }
    if (!selectedProduct)  { ToastService.error(tInvoice('select-product')); return }

    this.props.onSubmit({ selectedBrand, selectedGroup, selectedCategory, selectedProduct, selectedSaleType, quantity, unitPrice })
  }

  render() {
    const {
      brands, groups, categories, products, saleTypes,
      selectedBrand, selectedGroup, selectedCategory, selectedProduct, selectedSaleType,
    } = this.state
    const { createMode, onCancel, initialValues } = this.props

    return (
      <InvoiceProductForm
        brands={brands}
        groups={groups}
        categories={categories}
        products={products}
        saleTypes={saleTypes}
        selectedBrand={selectedBrand}
        selectedGroup={selectedGroup}
        selectedCategory={selectedCategory}
        selectedProduct={selectedProduct}
        selectedSaleType={selectedSaleType}
        initialQuantity={initialValues && initialValues.quantity}
        initialUnitPrice={initialValues && initialValues.unitPrice}
        onBrandChange={this.handleBrandChange}
        onGroupChange={this.handleGroupChange}
        onCategoryChange={this.handleCategoryChange}
        onProductChange={this.handleProductChange}
        onSaleTypeChange={this.handleSaleTypeChange}
        onSubmit={this.handleSubmit}
        onCancel={onCancel}
        createMode={createMode}
      />
    )
  }
}

const mapStateToProps = (state) => ({
  client: state.userReducer.client,
})

const mapDispatchToProps = (dispatch) => ({
  startFetch: () => dispatch(startFetch()),
  endFetch: () => dispatch(endFetch()),
})

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceProductFormContainer)
